import { useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { GroupConfigBreakoutsBanner } from "./GroupConfigBreakoutsBanner";
import { InPersonGroupTimerButton } from "./InPersonGroupTimerButton";

import { BreakoutDurationField } from "components/forms/BreakoutDurationField";
import { ReadOnlyBreakoutDuration } from "components/forms/ReadOnlyBreakoutDuration";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectActiveClassSessionId,
  selectCurrentSectionIsArchived,
  selectCurrentSectionIsLinked,
  selectIsClassSessionInPerson,
  selectIsEditModeEnabled,
  selectIsTimeline,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { useObjectSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { useAv } from "utils/av";
import { DEFAULT_BREAKOUT_DURATION } from "utils/breakouts";

export const PageBreakoutDuration = ({
  pageId,
  hasAssessments,
}: {
  pageId: string;
  hasAssessments: boolean;
}) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["breakout_duration"]);
  const { canAuthorSection } = useCapabilities();
  const isLinked = useAppSelector(selectCurrentSectionIsLinked);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const isTimeline = useAppSelector(selectIsTimeline);
  const isEditing = useAppSelector(selectIsEditModeEnabled);
  const activeSessionId = useAppSelector(selectActiveClassSessionId);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const isInPersonSession = useAppSelector(selectIsClassSessionInPerson);
  const { isInVirtualClass, isVirtualClassActive } = useAv();
  const canEdit =
    canAuthorSection &&
    !isLinked &&
    !isArchived &&
    !isTimeline &&
    !isInBreakout &&
    (activeSessionId && !isInPersonSession ? isInVirtualClass && isVirtualClassActive : isEditing);
  const duration = page?.breakout_duration ?? DEFAULT_BREAKOUT_DURATION;
  const form = useForm<{ breakout_duration: number | string }>({
    shouldFocusError: false,
    values: { breakout_duration: duration },
    resetOptions: { keepDirtyValues: true },
  });
  const pendingSave = useRef<Promise<number | null> | null>(null);

  // Start must wait for any automatic save already in flight.
  const saveDuration = () => {
    if (pendingSave.current) return pendingSave.current;
    if (!canEdit) return Promise.resolve(duration);
    let savedDuration: number | null = null;
    pendingSave.current = form
      .handleSubmit(async ({ breakout_duration }) => {
        try {
          const value = Number(breakout_duration);
          if (value !== duration) {
            // Keep the entered value retryable when the write fails.
            await storeApi.pages.partial_update(
              { id: pageId, breakout_duration: value },
              { skipToast: true, optimistic: false },
            );
          }
          form.reset({ breakout_duration: value });
          savedDuration = value;
        } catch (error) {
          toastLocalizedOperationCatcher("failed_to_save_breakout_duration")(error);
        }
      })()
      .then(() => {
        pendingSave.current = null;
        return savedDuration;
      });
    return pendingSave.current;
  };

  return (
    <FormProvider {...form}>
      <form
        className="flex w-full flex-col gap-2"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          saveDuration();
        }}
      >
        <GroupConfigBreakoutsBanner
          durationAction={<InPersonGroupTimerButton saveDuration={saveDuration} />}
          hasAssessments={hasAssessments}
          pageId={pageId}
          saveDuration={saveDuration}
        >
          <fieldset
            className="min-w-0"
            disabled={form.formState.isSubmitting}
            onBlur={(event) => {
              // Start actions save themselves; a failed blur save must not be
              // retried by the same click before starting the activity.
              if (
                event.relatedTarget instanceof HTMLElement &&
                event.relatedTarget.closest("[data-saves-duration]")
              )
                return;
              if (!event.currentTarget.contains(event.relatedTarget)) saveDuration();
            }}
          >
            {canEdit ? (
              <BreakoutDurationField layout="inline" onStep={saveDuration} />
            ) : (
              <ReadOnlyBreakoutDuration value={duration} />
            )}
          </fieldset>
        </GroupConfigBreakoutsBanner>
      </form>
    </FormProvider>
  );
};
